import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { ITaskByUserResponse, IUserScheduleSummary, TaskService } from "../../../../../services/task.service";
import { EmployeeCalendarModel } from "../../EmployeeCalendar.model";
import { PAGE_FAIL_RESPONSE } from "../../../../../resources-vue/vue-elements/Pagination/Pagination";
import { formatName } from "../../../../../common/format-name";
import eventBus from "../../../../../utilities/eventBus";

import Pagination from "../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import SummaryViewRow from "../SummaryViewRow/SummaryViewRow.vue";

export default defineComponent({
    components: {
        Pagination,
        SummaryViewRow
    },

    props: {
        employeeFilterOptions: {
            default: null
        }
    },

    data(vm) {
        return {
            _taskService: null as TaskService,
            isLoading: true as boolean,
            summaryPagination: null as any,
            pageSize: 15 as number,
            isError: false as boolean,
            employees: [],
            userIds: [],
            employeeScheduleSummary: [] as IUserScheduleSummary[],
            dateRangeList: [],
            tableHeaders: [],
            employeeSummary: [],
            payload: {} as any,
            Schedules: [] as ITaskByUserResponse[],
            columns: 11
        };
    },

    created() {
        this._taskService = Container.instance.get(TaskService);
        this.summaryPagination = this.$refs?.summaryPagination as typeof Pagination;
    },
    mounted() {
        this.summaryPagination = this.$refs?.summaryPagination as typeof Pagination;
        eventBus.on("callSummaryReset", this.pageReset);
        this.pageReset();
    },

    computed: {
        hasData() {
            return this.employees?.length > 0;
        }
    },

    methods: {
        async pageReset(payload?: any) {
            this.isError = false;
            if (payload) {
                this.payload = payload;
            } else {
                this.payload = this.employeeFilterOptions;
            }
            await this.summaryPagination?.reset();
        },
        async pageChanged(pageNumber: number, pageSize: number = 15) {
            try {
                if (
                    this.employeeFilterOptions?.agencies?.length > 0 &&
                    this.employeeFilterOptions.locationIds.length > 0
                ) {
                    this.isError = false;
                    this.employees = [];
                    this.employeeSummary = [];
                    this.employeeScheduleSummary = [];
                    this.dateRangeList = [];
                    this.tableHeaders = [];
                    this.payload.page = pageNumber;
                    this.isLoading = true;
                    let data = await this._taskService.getUsersAndTaskForCalendar({
                        ...this.payload,
                        AgencyIds: this.payload.agencies
                    });
                    if (data) {
                        this.userIds = [];
                        let dataItems = data.User.Items ?? [];
                        this.employees = dataItems.map((item: any) => new EmployeeCalendarModel(item));
                        this.employees?.forEach((user) => {
                            user.Agencies.forEach((op) => {
                                this.userIds.push(op.UserId);
                            });
                        });
                        this.Schedules = data.schedule;
                        await this.getEmployeeScheduleSummary(this.userIds);
                        this.getSummaryTableData();
                        return {
                            totalPageCount: data.User.PageCount,
                            success: data.User.ItemCount > 0,
                            totalCount: data.User.ItemCount
                        };
                    } else {
                        return PAGE_FAIL_RESPONSE;
                    }
                } else {
                    this.employees = [];
                    this.userIds = [];
                    return PAGE_FAIL_RESPONSE;
                }
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            }
        },
        async getEmployeeScheduleSummary(userIds: string[]) {
            try {
                const payload = {
                    StartDate: this.employeeFilterOptions.startDate,
                    EndDate: this.employeeFilterOptions.endDate,
                    AgencyIds: this.employeeFilterOptions.agencies,
                    UserIds: userIds
                };
                const response = await this._taskService.getUserScheduleSummary(payload);
                this.employeeScheduleSummary = response.Items;
            } catch (err) {
                console.error(err);
            }
        },
        getSummaryTableData() {
            let startDate = moment(this.employeeFilterOptions.startDate);
            let endDate = moment(this.employeeFilterOptions.endDate);
            while (startDate.isSameOrBefore(endDate)) {
                this.tableHeaders.push(moment(startDate).format("ddd MM/DD"));
                startDate.add(1, "day");
            }
            this.employeeSummary = [];
            this.employees.forEach((emp: any) => {
                let userSummaryData = [];
                let empSchedules = [];
                emp.Agencies.map((user: any) => {
                    userSummaryData = [
                        ...userSummaryData,
                        ...this.employeeScheduleSummary.filter(
                            (summary: any) => summary.UserId === user.UserId && summary.AgencyId === user.AgencyId
                        )
                    ];
                    let userSchedules = this.Schedules.filter(
                        (schedule) =>
                            schedule.UserId === user.UserId &&
                            !schedule.IsNonVisitAssignment &&
                            schedule.AgencyId === user.AgencyId
                    );
                    userSchedules = userSchedules.map((schedule) => {
                        return {
                            ...schedule,
                            AgencyName: user.AgencyName
                        };
                    });
                    empSchedules = [...empSchedules, ...userSchedules];
                });
                userSummaryData = Object.values(
                    userSummaryData.reduce((acc, item) => {
                        const key = item.Date;

                        if (!acc[key]) {
                            acc[key] = {
                                date: key,
                                TotalHours: 0,
                                TotalVisits: 0
                            };
                        }

                        acc[key].TotalHours += item.TotalHours;
                        acc[key].TotalVisits += item.TotalVisits;

                        return acc;
                    }, {})
                );
                const result = userSummaryData.reduce(
                    (acc, obj) => {
                        acc.totalHours += obj.TotalHours;
                        acc.totalVisits += obj.TotalVisits;
                        return acc;
                    },
                    { totalHours: 0, totalVisits: 0 }
                );
                let fullName = formatName(emp.FirstName, emp.LastName, "LN, FN");
                this.employeeSummary.push({
                    id: emp.Id,
                    name: fullName,
                    firstName: emp.FirstName,
                    lastName: emp.LastName,
                    userSummaryData: [...userSummaryData],
                    totalHours: result.totalHours,
                    totalVisits: result.totalVisits,
                    schedules: empSchedules
                });
            });
            this.tableHeaders = [...new Set(this.tableHeaders)];
            this.isLoading = false;
        }
    }
});
